import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { baseUrl } from '../utils';
import axios from 'axios';

const initialState = {
  loading: false,
  openVisits: {
    content: [],
  },
  data: {
    content: [],
  },
  prescriptionDetail: {
    medicines: [],
  },
  allPrescriptions: [],
  prescriptionStatus: {},
  prescriptionLoading: false,
};

export const getPrescriptionVisits = createAsyncThunk(
  'getVisits/prescription',
  async (pagination) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      const response = await axios.get(
        `${baseUrl}/provider/visit?status=Open&sort=visitCreatedOn,desc&screen=Prescription&page=${pagination?.page}&size=${pagination?.pageSize}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const getSubmittedPrescriptions = createAsyncThunk(
  'getSubmittedPrescriptions',
  async (pagination) => {
    let token = localStorage.getItem('x-auth-token');

    try {
      // Use /provider/rx/search (authorized for providers); /provider/rx/ 403s
      // with "Unauthorized account access", so submitted clinical records never showed.
      const response = await axios.get(
        `${baseUrl}/provider/rx/search?sort=createdTimestamp,desc&page=${pagination?.page}&size=${pagination?.pageSize}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const searchSubmittedPrescriptions = createAsyncThunk(
  'searchSubmittedPrescriptions',
  async ({ page, query }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      const response = await axios.get(`${baseUrl}/provider/rx/search?query=${query}`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const createPrescription = createAsyncThunk(
  'createPrescription/new',
  async ({ updatedValue, navigate, openNotificationWithIcon, setLoading }) => {
    let token = localStorage.getItem('x-auth-token');
    setLoading(true);
    try {
      const { data } = await axios.post(`${baseUrl}/provider/rx`, updatedValue, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (data?.api?.responseCode === 2010) {
        openNotificationWithIcon('success', data.message);
        navigate(`/clinical_information/${data?.result?.prescription_id}`);
      } else {
        openNotificationWithIcon('error', data.message);
      }
      setLoading(false);

      return data;
    } catch (error) {
      setLoading(false);
      openNotificationWithIcon('error', error?.response?.data?.message || error.message);
      console.log(error);
    }
  },
);

// Screen 1 (Clinical Module) — saves structured clinical data to the new
// clinical-record endpoint. Diagnosis codes are sent as `icd_code` (the field
// the dev API expects; the reference object's `icd10_code` is NOT used).
// Medicines are included per the canonical payload object — confirm with
// backend whether they persist here or need a separate prescription call.
export const createClinicalRecord = createAsyncThunk(
  'createClinicalRecord',
  async ({ payload, navigate, openNotificationWithIcon, setLoading }) => {
    let token = localStorage.getItem('x-auth-token');
    setLoading(true);
    try {
      const { data } = await axios.post(`${baseUrl}/provider/rx/clinical-record`, payload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        skipErrorToast: true,
      });

      const ok =
        data?.success === true ||
        data?.status === 200 ||
        String(data?.api?.responseCode ?? '').startsWith('2');
      if (ok) {
        openNotificationWithIcon('success', data.message || 'Clinical details saved');
        // Route to the clinical record just saved (the records page), not open visits.
        const rxId = data?.result?.prescription_id;
        navigate(rxId ? `/clinical_information/${rxId}` : '/clinical_information/submitted');
      } else {
        openNotificationWithIcon('error', data.message || 'Failed to save clinical details');
      }
      setLoading(false);
      return data;
    } catch (error) {
      setLoading(false);
      openNotificationWithIcon('error', error?.response?.data?.message || error.message);
      console.log(error);
    }
  },
);

// Screen 12 — optical shop marks a member's active Rx as dispensed.
// PATCH /provider/rx/{id}/status/DISPENSED
export const markPrescriptionDispensed = createAsyncThunk(
  'markPrescriptionDispensed',
  async ({ prescriptionId, openNotificationWithIcon, setLoading, onDone }) => {
    let token = localStorage.getItem('x-auth-token');
    if (setLoading) setLoading(true);
    try {
      const { data } = await axios.patch(
        `${baseUrl}/provider/rx/${prescriptionId}/status/DISPENSED`,
        {},
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const ok =
        data?.success === true ||
        data?.status === 200 ||
        String(data?.api?.responseCode ?? '').startsWith('2');
      if (ok) {
        if (openNotificationWithIcon)
          openNotificationWithIcon('success', data.message || 'Prescription marked as dispensed');
        if (onDone) onDone();
      } else if (openNotificationWithIcon) {
        openNotificationWithIcon('error', data.message || 'Failed to mark as dispensed');
      }
      if (setLoading) setLoading(false);
      return data;
    } catch (error) {
      if (setLoading) setLoading(false);
      if (openNotificationWithIcon) openNotificationWithIcon('error', error.message);
      console.log(error);
    }
  },
);

export const getPrescriptionDetail = createAsyncThunk('getClaimDetail', async (id) => {
  let token = localStorage.getItem('x-auth-token');
  try {
    const response = await axios.get(`${baseUrl}/provider/rx/${id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.log(error);
  }
});

export const searchPrescription = createAsyncThunk('searchPrescription', async (searchItem) => {
  let token = localStorage.getItem('x-auth-token');
  const searchObj = {};
  if (searchItem.search) {
    const key = Object.keys(searchItem.search);
    if (key[0] === 'Prescription ID') {
      searchObj.prescriptionId = Object.values(searchItem.search)[0];
    } else if (key[0] === 'Visit ID') {
      searchObj.visitId = Object.values(searchItem.search)[0];
    } else if (key[0] === 'Member No.') {
      searchObj.memberNumber = Object.values(searchItem.search)[0];
    } else if (key[0] === 'Patient Name') {
      searchObj.patientName = Object.values(searchItem.search)[0];
    }
  }
  let filterStr = '';
  if (searchItem.filters) {
    const keys = Object.keys(searchItem.filters);
    if (keys.indexOf('startDate') !== -1) {
      filterStr = filterStr + `&fromDate=${searchItem.filters['startDate']}`;
    }
    if (keys.indexOf('endDate') !== -1) {
      filterStr = filterStr + `&toDate=${searchItem.filters['endDate']}`;
    }
    if (keys.indexOf('doctor') !== -1) {
      filterStr = filterStr + `&attendingDoctorName=${searchItem.filters['doctor']}`;
    }
  }

  try {
    const response = await axios.get(
      `${baseUrl}/provider/rx/search?${
        Object.keys(searchObj).length > 0
          ? `${Object.keys(searchObj)[0]}=${Object.values(searchObj)[0]} `
          : ''
      }${filterStr}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
});

export const searchOpenPrescription = createAsyncThunk(
  'searchOpenPrescription',
  async (searchItem) => {
    let token = localStorage.getItem('x-auth-token');
    const searchObj = {};
    if (searchItem.search) {
      const key = Object.keys(searchItem.search);
      if (key[0] === 'Visit ID') {
        searchObj.visitId = Object.values(searchItem.search)[0];
      } else if (key[0] === 'Member No.') {
        searchObj.memberNumber = Object.values(searchItem.search)[0];
      } else if (key[0] === 'Patient Name') {
        searchObj.memberName = Object.values(searchItem.search)[0];
      }
    }
    let filterStr = '';
    if (searchItem.filters) {
      const keys = Object.keys(searchItem.filters);
      if (keys.indexOf('startDate') !== -1) {
        filterStr = filterStr + `&fromDate=${searchItem.filters['startDate']}`;
      }
      if (keys.indexOf('endDate') !== -1) {
        filterStr = filterStr + `&toDate=${searchItem.filters['endDate']}`;
      }
      if (keys.indexOf('doctor') !== -1) {
        filterStr = filterStr + `&attendingDoctorName=${searchItem.filters['doctor']}`;
      }
    }

    try {
      const response = await axios.get(
        `${baseUrl}/provider/visit/search-visit?status=Open&screen=Prescription&${
          Object.keys(searchObj).length > 0
            ? `${Object.keys(searchObj)[0]}=${Object.values(searchObj)[0]} `
            : ''
        }${filterStr}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const changePrescription = createAsyncThunk(
  'changePrescription',
  async ({
    formData,
    openNotificationWithIcon,
    setPrescriptionLoading,
    setOpenRequestPreAuth,
    form,
  }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      setPrescriptionLoading(true);
      const { data } = await axios.post(`${baseUrl}/pharmacy/pre-authorisation`, formData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (data?.api?.responseCode === 2010) {
        setPrescriptionLoading(false);
        setOpenRequestPreAuth(false);
        openNotificationWithIcon('success', data.message);
        form.resetFields();
      } else if (data?.api?.responseCode === 3530) {
        setPrescriptionLoading(false);
        setOpenRequestPreAuth(false);
        openNotificationWithIcon('error', data.message);
        form.resetFields();
      } else {
        setPrescriptionLoading(true);
        openNotificationWithIcon('error', data.message);
      }

      return data;
    } catch (error) {
      openNotificationWithIcon('error', error?.response?.data?.message || error.message);
      setPrescriptionLoading(false);
      setOpenRequestPreAuth(false);
      console.log(error);
    }
  },
);

export const changeManualPrescription = createAsyncThunk(
  'changeManualPrescription',
  async ({
    formData,
    openNotificationWithIcon,
    setPrescriptionLoading,
    setOpenRequestPreAuth,
    form,
  }) => {
    let token = localStorage.getItem('x-auth-token');
    try {
      setPrescriptionLoading(true);
      const { data } = await axios.post(`${baseUrl}/pharmacy/manual/pre-authorisation`, formData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (data?.api?.responseCode === 2010) {
        setPrescriptionLoading(false);
        setOpenRequestPreAuth(false);
        openNotificationWithIcon('success', data.message);
        form.resetFields();
      } else if (data?.api?.responseCode === 3530) {
        setPrescriptionLoading(false);
        setOpenRequestPreAuth(false);
        openNotificationWithIcon('error', data.message);
        form.resetFields();
      } else {
        setPrescriptionLoading(true);
        openNotificationWithIcon('error', data.message);
      }

      return data;
    } catch (error) {
      openNotificationWithIcon('error', error?.response?.data?.message || error.message);
      setPrescriptionLoading(false);
      setOpenRequestPreAuth(false);
      console.log(error);
    }
  },
);

export const getAllPrescriptions = createAsyncThunk('getAllPrescriptions', async ({ filters }) => {
  let token = localStorage.getItem('x-auth-token');

  let params = '';
  if (filters.status) {
    params = `status=${filters.status}`;
  }
  try {
    const response = await axios.get(
      `${baseUrl}/pharmacy/pre-authorisation${params ? `?${params}` : ''}`,
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
});

export const getPrescriptionStatus = createAsyncThunk(
  'getPrescriptionStatus',
  async ({ preAuthorisationId }) => {
    let token = localStorage.getItem('x-auth-token');

    try {
      const response = await axios.get(
        `${baseUrl}/pharmacy/pre-authorisation/${preAuthorisationId}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.log(error);
    }
  },
);

export const prescriptionSlice = createSlice({
  name: 'prescription',
  initialState: initialState,
  extraReducers(builder) {
    builder.addCase(getPrescriptionVisits.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPrescriptionVisits.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.openVisits = action.payload?.result;
      } else {
        state.openVisits = {
          content: [],
        };
      }
    });
    builder.addCase(getPrescriptionVisits.rejected, (state) => {
      state.loading = false;
      state.openVisits = {
        content: [],
      };
    });
    builder.addCase(getSubmittedPrescriptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getSubmittedPrescriptions.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.data = action.payload?.result;
      } else {
        state.data = {
          content: [],
        };
      }
    });
    builder.addCase(getSubmittedPrescriptions.rejected, (state) => {
      state.loading = false;
      state.data = {
        content: [],
      };
    });
    builder.addCase(getPrescriptionDetail.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPrescriptionDetail.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.prescriptionDetail = action.payload?.result;
      } else {
        state.loading = true;
        state.prescriptionDetail = {};
      }
    });
    builder.addCase(getPrescriptionDetail.rejected, (state) => {
      state.loading = false;
      state.prescriptionDetail = {};
    });

    builder.addCase(searchSubmittedPrescriptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchSubmittedPrescriptions.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.data.content = action.payload?.result;
        state.data.property = null;
      } else {
        state.data = {
          content: [],
        };
      }
    });
    builder.addCase(searchSubmittedPrescriptions.rejected, (state) => {
      state.loading = false;
      state.data = {
        content: [],
      };
    });
    // search prescription
    builder.addCase(searchPrescription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchPrescription.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.data = action.payload?.result;
      } else {
        state.data = {
          content: [],
        };
      }
    });
    builder.addCase(searchPrescription.rejected, (state) => {
      state.loading = false;
      state.data = {
        content: [],
      };
    });

    // search open prescription
    builder.addCase(searchOpenPrescription.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(searchOpenPrescription.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload?.result) {
        state.openVisits = action.payload?.result;
      } else {
        state.openVisits = {
          content: [],
        };
      }
    });
    builder.addCase(searchOpenPrescription.rejected, (state) => {
      state.loading = false;
      state.data = {
        content: [],
      };
    });

    //
    builder.addCase(changePrescription.pending, (state) => {
      state.prescriptionLoading = true;
    });
    builder.addCase(changePrescription.fulfilled, (state, action) => {
      state.prescriptionLoading = false;
    });
    builder.addCase(changePrescription.rejected, (state) => {
      state.prescriptionLoading = false;
    });
    //
    builder.addCase(changeManualPrescription.pending, (state) => {
      state.prescriptionLoading = true;
    });
    builder.addCase(changeManualPrescription.fulfilled, (state, action) => {
      state.prescriptionLoading = false;
    });
    builder.addCase(changeManualPrescription.rejected, (state) => {
      state.prescriptionLoading = false;
    });
    //
    builder.addCase(getAllPrescriptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllPrescriptions.fulfilled, (state, action) => {
      if (action?.payload?.api?.responseCode === 2040) {
        state.allPrescriptions = action?.payload?.result?.content;
      } else {
        state.allPrescriptions = [];
      }
      state.loading = false;
    });
    builder.addCase(getAllPrescriptions.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getPrescriptionStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPrescriptionStatus.fulfilled, (state, action) => {
      if (action?.payload?.api?.responseCode === 2040) {
        state.prescriptionStatus = action?.payload?.result;
      }
      state.loading = false;
    });
    builder.addCase(getPrescriptionStatus.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const { resetMember, addMemberError } = prescriptionSlice.actions;

export default prescriptionSlice.reducer;
